import { useState } from "react";
import { styles } from "../../../utils/styles/products-car-styles";
import { useI18n } from "@/i18n/I18nProvider";
import Box from "@mui/material/Box";
import dynamic from "next/dynamic";
const PageHead = dynamic(() => import("@/components/PageHead"));
import ProductMain from "@/components/products-revamp/product-main";
import StepsRow from "@/components/steps-row";
import ApplyProduct from "@/components/products-revamp/product-apply";
import DynamicFAQSection from "@/components/DynamicFAQSection";
import WhyContact from "@/components/products-revamp/why-contact";
import { colors } from "@/utils/const";
import CalcPopup from "@/components/products-revamp/CalcPopup";
import Image from "next/image";
import CenterTitle from "@/components/center-title";
import CustomText from "../../../components/text";
import { ProductsPlans } from "@/components/products-revamp/products-plans";
import LogosMarquee from "@/components/logos-marquee-slider";
import {
  getAutoModels,
  getInstallmentsPlans,
  getProductPageData,
} from "@/services/get-product-data";
import Features from "@/components/products-revamp/features-section";
import PgWithImgSection from "@/components/products-revamp/pg-with-img-section";
import SplitPgSection from "@/components/products-revamp/split-pg-section";
import { getLocalizedValue } from "@/utils/general-functions";
import { getStaticTextByKey } from "@/utils/general-functions";
import { useSplitContentSections } from "@/hooks/product";
import { useProductLoanPackages } from "@/hooks/useProductLoanPackages";
import { getProductsText } from "../../../../locales/products";

export default function Index({
  heroData,
  contentWithImageData,
  faqData,
  stepsData,
  carModels,
  plansData,
  whycontactData,
  staticTextData,
  featuresData,
  mainData,
}) {
  const { t, locale } = useI18n();
  const products = getProductsText(locale);

  const staticTexts = getStaticTextByKey(staticTextData?.data);
  const { fullNavySections, splitSections } =
    useSplitContentSections(contentWithImageData);
  const { targetProduct, data } = useProductLoanPackages(products.cars.id);
  const [openCalc, setOpenCalc] = useState(false);
  return (
    <div>
      <PageHead
        title={getLocalizedValue(mainData[0], "meta_title", locale)}
        description={getLocalizedValue(mainData[0], "meta_description", locale)}
        scripts={getLocalizedValue(mainData[0], "schema", locale)}
      />
      <Box sx={{ ...styles.container, overflow: "hidden" }}>
        {heroData?.length > 0 && (
          <ProductMain
            heroData={heroData}
            staticTexts={staticTexts}
            onApplyClick={() => {
              const formSection = document.getElementById("apply-form");
              if (formSection) {
                formSection.scrollIntoView({ behavior: "smooth" });
              }
            }}
            onCalcClick={() => setOpenCalc((prev) => !prev)}
            onExploreMoreClick={() => {
              const formSection = document.getElementById("auto-features");
              if (formSection) {
                formSection.scrollIntoView({ behavior: "smooth" });
              }
            }}
            imgStyle={{
              mt: locale === "ar" ? { xs: 5, xl: 7 } : { xs: 5, xl: 6 },
            }}
            headerH1={true}
            shadow={true}
          />
        )}
        {fullNavySections?.length > 0 &&
          fullNavySections.map((section, index) => (
            <PgWithImgSection
              key={section.id || index}
              img={section.img}
              alt={section.alt_img}
              title={section.title?.text?.[locale]}
              titleType={section.title?.type}
              paragraph={section.description?.text?.[locale]}
              paragraphType={section.description?.type}
            />
          ))}

        {splitSections?.length > 0 &&
          splitSections.map((section, index) => (
            <SplitPgSection
              key={section.id || index}
              img={section.img}
              alt={section.alt_img}
              title={section.title?.text?.[locale]}
              titleType={section.title?.type}
              paragraph={section.description?.text?.[locale]}
              paragraphType={section.description?.type}
              bgHeight={{
                xs: "100%",
                md: locale === "ar" ? "50%" : "54%",
              }}
            />
          ))}
        <Box sx={{ position: "relative", bgcolor: colors.white }}>
          <WhyContact whycontactData={whycontactData} />
          {/***********models */}
          {carModels?.length > 0 &&
            carModels.map((section, index) => (
              <Box key={section?.id || index} className="vertical-safe-padding">
                <CenterTitle
                  title={section?.title?.text?.[locale]}
                  subtitle={section?.description?.text?.[locale]}
                  title_variant={section?.title?.type}
                  subtitle_variant={section?.description?.type}
                />

                <LogosMarquee
                  modelsData={section?.models}
                  height={180}
                  breakpoints={{
                    0: { slidesPerView: 3 },
                    650: { slidesPerView: 5 },
                    920: { slidesPerView: 7 },
                    1200: { slidesPerView: 10 },
                    1600: { slidesPerView: 12 },
                  }}
                  renderItem={({ icon, title, alt }) => (
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                      }}
                    >
                      <Image
                        quality={100}
                        src={icon}
                        alt={alt || title?.text?.[locale] || "model logo"}
                        style={{
                          display: "block",
                          margin: "auto",
                          objectFit: "contain",
                        }}
                        width={80}
                        height={80}
                      />

                      <CustomText
                        text={title?.text?.[locale]}
                        type="xxs"
                        customStyle={{ fontWeight: "600" }}
                      />
                    </Box>
                  )}
                />

                <CenterTitle
                  subtitle={section?.footer_text?.text?.[locale]}
                  subtitle_variant={section?.footer_text?.type}
                />
              </Box>
            ))}
          {/**Features section */}
          {featuresData?.length > 0 &&
            featuresData.map((section, index) => (
              <Features
                key={section?.id || index}
                section={section}
                bgHeight={{ xs: "60%", sm: "65%", md: "40%", lg: "35%" }}
                id="auto-features"
              />
            ))}
        </Box>
        {/** installmentPlans Section*/}
        {/* {plansData.length > 0 && (
          <Box className="vertical-safe-padding">
            <ProductsPlans
              sectionTitle={
                staticTexts.installments_plans_title.value.text[locale]
              }
              items={plansData?.[0].plans}
              cardBgColor={colors.dark_yellow}
              cardColor={colors.navy}
            />
          </Box>
        )} */}

        {/**faq section */}
        {faqData?.length > 0 &&
          faqData.map((section, index) => (
            <DynamicFAQSection
              key={section?.id || index}
              title={staticTexts?.faqs_section_title?.value?.text[locale] || ""}
              items={section?.faqs}
              renderHTML={true}
              variantTitle="h2"
              variantQ="h3"
            />
          ))}

        {/**Steps section */}
        {stepsData?.length > 0 &&
          stepsData.map((section, index) => (
            <StepsRow
              key={section.id || index}
              title={section?.title?.text?.[locale]}
              titleType={section?.title?.type}
              subtitle={section?.description?.text?.[locale]}
              subtitleType={section?.description?.type}
              steps={section?.items}
            />
          ))}
        <ApplyProduct
          img={heroData[0]?.img}
          title={mainData[0]?.form_title.text[locale]}
          titleType={mainData[0]?.form_title?.type}
          description={mainData[0]?.form_desc.text[locale]}
          descType={mainData[0]?.form_desc?.type}
          formType="a"
          product="auto"
          source={29}
          shadow={true}
        />

        {targetProduct?.hasPlan && (
          <CalcPopup
            open={openCalc}
            onClose={() => setOpenCalc(false)}
            data={data}
            max_amount={targetProduct?.maxLimit}
            price_label={products.cars.price}
          />
        )}
      </Box>
    </div>
  );
}
export async function getServerSideProps() {
  try {
    const data = await getProductPageData("auto");

    const [carModels, installmentsPlans] = await Promise.all([
      getAutoModels(),
      getInstallmentsPlans("auto"),
    ]);

    return {
      props: {
        ...data,
        carModels: carModels?.autoModels || [],
        plansData: installmentsPlans?.installmentsPlans || [],
      },
    };
  } catch {
    return {
      props: {
        ...emptyProps,
        carModels: [],
        plansData: [],
      },
    };
  }
}
